import { RefObject, useLayoutEffect } from 'react';

/**
 * 가로 넘김 트랙의 높이를 "지금 보이는 장" 높이에 맞춘다(홈 프리미엄 2단 — 카드가 한 줄뿐인 장은 1단 높이).
 * 트랙의 자식 하나가 한 장(트랙 폭과 같은 폭)이고, 자식은 `align-items: flex-start` 로 제 높이를 가져야 한다.
 * 높이는 스크롤 위치에 따라 두 장 사이를 이어서 바꾼다 — 넘기는(손가락·부드러운 스크롤) 동안 같이 줄고 늘어
 * 아래 내용이 한 번에 튀지 않고, 스와이프 중 높이 상태를 다시 그리지 않아 깜박이지 않는다.
 * `enabled` 가 false 면 높이를 건드리지 않는다(1단·채용 목록은 예전 그대로).
 */
export function useFollowPageHeight(track: RefObject<HTMLElement>, enabled: boolean, contentKey: string) {
    useLayoutEffect(() => {
        const el = track.current;
        if (!el) return;
        if (!enabled) {
            el.style.height = '';
            return;
        }
        const update = () => {
            const pages = Array.from(el.children) as HTMLElement[];
            if (pages.length === 0 || el.clientWidth === 0) return;
            const position = Math.max(0, Math.min(pages.length - 1, el.scrollLeft / el.clientWidth));
            const index = Math.floor(position);
            // offsetHeight 는 정수로 반올림돼 카드(228.3px 등) 아래 테두리가 0.3px 잘린다 — 소수 높이를 쓴다.
            const from = pages[index].getBoundingClientRect().height;
            const to = pages[Math.min(index + 1, pages.length - 1)].getBoundingClientRect().height;
            const height = `${(from + (to - from) * (position - index)).toFixed(2)}px`;
            if (el.style.height !== height) el.style.height = height;
        };
        update();
        el.addEventListener('scroll', update, { passive: true });
        // 폭이 바뀌거나(열 수·글 줄바꿈) 이미지가 늦게 떠 장 높이가 바뀌어도 다시 맞춘다.
        const observer = new ResizeObserver(update);
        observer.observe(el);
        Array.from(el.children).forEach(page => observer.observe(page));
        return () => {
            el.removeEventListener('scroll', update);
            observer.disconnect();
            el.style.height = '';
        };
    }, [track, enabled, contentKey]);
}
